import { Box, Button, Checkbox, FormControlLabel, Grid, Stack, Typography } from '@mui/material'
import { ChangeEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import usersService from '../../../services/usersService'
import CardTAG from '../../CardTAG'
import TextInput from '../../TextInput'
import FormLayout from '../FormLayout'
import { IAddUserForm, INewUser } from './types'
import { ReactComponent as PencilIcon } from '../../../assets/images/PencilIcon.svg'
import colors from '../../../constants/colors'
import LabelErrorTAG from '../../LabelErrorTAG'
import textUtils from '../../../utils/textUtils'
import { usersReportRoles } from '../../../store/users/types'
import { INVESTIGATOR } from '../../../utils/userRoleTypeUtils'

const defaulfFormValues: INewUser = {
  user: {
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    is_active: 1,
    type: usersReportRoles.TEAM_MEMBER,
  },
  userprofile: {
    organization: 1,
    roles: [1],
    profile_pic: '',
  },
}

const AddUserForm: React.FC<IAddUserForm> = ({ onClose, onSubmit, userData = undefined }) => {
  const [userRoles, setUserRoles] = useState<{ label: string; value: number }[]>([])
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [changedRoles, setChangedRoles] = useState<number[]>([])

  const fetchUserRoles = async () => {
    const res = await usersService.getUsersRoles()
    // console.log(res.data);
    const newRoles = res.data.map((item: { id: number; text: string }) => {
      return { label: item.text, value: item.id }
    })

    const matchingIds = newRoles
      .filter((obj: { label: any }) => userData?.userprofile?.roles?.includes(obj.label))
      .map((obj: { value: any }) => obj.value)

    if(matchingIds.length === 0 || !matchingIds.includes(INVESTIGATOR.id)){
      matchingIds.push(INVESTIGATOR.id)
    } 

    setValue('userprofile.roles', matchingIds)
    setUserRoles(newRoles)
    setChangedRoles(matchingIds)
  }
  const defaultValues = userData ?? defaulfFormValues
  const formMethods = useForm({
    defaultValues,
    mode: 'onChange',
    shouldFocusError: false,
  })
  const { register, handleSubmit, watch, setValue, formState: { errors },} = formMethods
  const rolesWatchValue = watch('userprofile.roles') as unknown as number[]
  const imageWatchValue = watch('userprofile.profile_pic')
  
  useEffect(() => {
    if (typeof imageWatchValue === 'string') {
      setImagePreview(imageWatchValue === '' ? '' : imageWatchValue)
      return
    }
    if (imageWatchValue instanceof File) {
      setImagePreview(URL.createObjectURL(imageWatchValue))
    }
  }, [imageWatchValue])

  useEffect(() => {
    fetchUserRoles()
  }, [])

  const checkboxRoleHandler = (event: React.ChangeEvent<HTMLInputElement>, roleId: number) => {
    let change = []
    if (!event.target.checked) {
      change = rolesWatchValue.filter(role => role !== roleId)
    }else{
      change = [...rolesWatchValue, roleId]
    }
    
    setValue('userprofile.roles', change)
    setChangedRoles(change)
  }
 
  const renderUserRoles = () => {
    return userRoles.map(role => {
      return (
        <FormControlLabel
          control={
            <Checkbox
              onChange={e => {
                checkboxRoleHandler(e, role.value)
              }}
              checked={changedRoles.includes(role.value)}
              // disabled={role.value === INVESTIGATOR.id}
            />
          }
          label={
            <Typography variant='bodyRegular' sx={{ textTransform: 'capitalize' }}>
              {textUtils.humanize(role.label)}
            </Typography>
          }
          key={role.value}
        />
      )
    })
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      const file = e?.target?.files[0]
      setValue('userprofile.profile_pic', file) // Set the value of the "file" field to the selected File object
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CardTAG
        title={userData ? 'Edit User' : 'Add New User'}
        closable={true}
        closeCard={() => {
          onClose(false)
        }}
      >
        <Grid container xs={12} spacing={2}>
          <Grid item xs={12} sm={12}>
            <FormLayout>
              <TextInput
                register={register}
                fieldName='user.email'
                label='Primary Email'
                error={(errors as any)?.user?.email?.message}
                validation={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address',
                  },
                }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: '1.8rem' }}>
                <Stack
                  sx={{
                    height: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    maxHeight: '30vh',
                  }}
                >
                  {!imagePreview ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '12rem',
                        height: '12rem',
                        border: '1px solid #ccc',
                        borderRadius: '50%',
                      }}
                    >
                      <Button
                        component='label'
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                      >
                        <Typography sx={{ textAlign: 'center', color: colors.TAG_TEXT_GRAY }}>
                          Add Photo
                        </Typography>
                        <input
                          onChange={handleFileChange}
                          hidden
                          multiple
                          accept='image/*'
                          type='file'
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            backgroundColor: 'transparent',
                            border: 'none',
                          }}
                        />
                        <PencilIcon style={{ position: 'absolute', bottom: 0, right: 0 }} />
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '12rem',
                        height: '12rem',
                        border: '1px solid #ccc',
                        borderRadius: '50%',
                      }}
                    >
                      {' '}
                      <Button
                        component='label'
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          backgroundColor: 'transparent',
                          border: 'none',
                          padding: 0,
                        }}
                      >
                        <input
                          onChange={handleFileChange}
                          hidden
                          multiple
                          // accept='image/*'
                          type='file'
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            backgroundColor: 'transparent',
                            border: 'none',
                            zIndex: 10,
                          }}
                        />
                        <PencilIcon style={{ position: 'absolute', bottom: 0, right: 0 }} />
                        <img
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '50%',
                          }}
                          src={imagePreview}
                        />
                      </Button>
                    </Box>
                  )}
                </Stack>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '1.8rem' }}>
                  <TextInput
                    register={register}
                    fieldName='user.first_name'
                    label='First Name'
                    error={(errors as any)?.user?.first_name?.message}
                    validation={{                               // ✅ ADD VALIDATION
                      required: 'First name is required',
                      minLength: {
                        value: 2,
                        message: 'First name must be at least 2 characters',
                      },
                    }}
                  />
                  <TextInput
                    register={register}
                    fieldName='user.last_name'
                    label='Last Name'
                    error={(errors as any)?.user?.last_name?.message}
                    validation={{                              // ✅ ADD VALIDATION
                      required: 'Last name is required',
                      minLength: {
                        value: 2,
                        message: 'Last name must be at least 2 characters',
                      },
                    }}
                  />
                </Box>
              </Box>

              <TextInput
                register={register}
                fieldName='userprofile.title'
                label='Title'
              ></TextInput>
              {userRoles && (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <LabelErrorTAG label='Roles' />
                  {renderUserRoles()}
                </Box>
              )}
              <Button variant='submitFormButton' type='submit'>
                <Typography variant='bodyBold'>{userData ? 'Save Changes' : 'Add User'}</Typography>
              </Button>
            </FormLayout>
          </Grid>
        </Grid>
      </CardTAG>
    </form>
  )
}

export default AddUserForm
